/* eslint-disable prefer-const */
import CircularProgress from '@material-ui/core/CircularProgress';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import { RocketLaunch } from '@phosphor-icons/react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import useResponseUser from '../../../../../../hooks/useResponseUser';
import * as aiService from '../../../../../../services/ai.service';
import { getBeneficiaryById } from '../../../../../../services/user.service';
import { getFromLS } from '../../../../../../utils/utils';
import { useIASynthesisStyles } from './iasynthesis.styles';

type IASynthesisBlockProps = {
  userId: string;
  responseTestId: string;
  testName: string;
};

const SparkleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true">
    <path
      d="M8 1.5L9.1 6.4L14 7.5L9.1 8.6L8 13.5L6.9 8.6L2 7.5L6.9 6.4L8 1.5Z"
      fill="currentColor"
    />
  </svg>
);

const IASynthesisBlock: FC<IASynthesisBlockProps> = ({ userId, responseTestId, testName }) => {
  const classes = useIASynthesisStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [synthesisText, setSynthesisText] = useState<string | null>(null);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const [isCheckingExistence, setIsCheckingExistence] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const textContentRef = useRef<HTMLDivElement>(null);
  const { getResponseTestUser } = useResponseUser();

  const checkTextHeight = useCallback(() => {
    if (!textContentRef.current || !synthesisText || isTextExpanded) return;

    const element = textContentRef.current;
    const rect = element.getBoundingClientRect();

    if (rect.height === 0) {
      setTimeout(checkTextHeight, 50);
      return;
    }

    const originalMaxHeight = element.style.maxHeight;
    const originalOverflow = element.style.overflow;

    element.style.maxHeight = 'none';
    element.style.overflow = 'visible';
    const naturalHeight = element.scrollHeight;
    element.style.maxHeight = originalMaxHeight;
    element.style.overflow = originalOverflow;

    setIsTextOverflowing(naturalHeight > 296);
  }, [synthesisText, isTextExpanded]);

  useEffect(() => {
    const checkExistingSynthesis = async () => {
      try {
        const result = await aiService.checkTestSynthesis(responseTestId);
        if (result?.exists && result.synthesis) {
          setSynthesisText(result.synthesis);
          setTimeout(checkTextHeight, 100);
        }
      } catch {
        // Missing synthesis or endpoint failure — show generate CTA
      } finally {
        setIsCheckingExistence(false);
      }
    };

    checkExistingSynthesis();
  }, [responseTestId, checkTextHeight]);

  const handleGenerateSynthesis = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const token = getFromLS('userToken') || '';
      const [identityResponse, responseTest] = await Promise.all([
        getBeneficiaryById({ userId, token, needTestDone: false }),
        getResponseTestUser(responseTestId),
      ]);

      const synthesis = await aiService.generateTestSynthesis(
        userId,
        responseTestId,
        testName,
        identityResponse.data,
        responseTest
      );

      setSynthesisText(synthesis);
      setTimeout(checkTextHeight, 100);
    } catch (error: any) {
      if (
        error?.message?.includes('SYNTHESIS_REFUSED') ||
        error?.response?.data?.message?.includes('SYNTHESIS_REFUSED')
      ) {
        setErrorMessage(
          'La génération de la synthèse a été refusée par notre système de sécurité. Certaines données de votre test ont déclenché nos filtres de modération. Veuillez réessayer ultérieurement ou contacter notre support si le problème persiste.'
        );
      } else {
        setErrorMessage(
          'Une erreur est survenue lors de la génération de votre synthèse. Veuillez réessayer ultérieurement.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (synthesisText) {
    const shouldShowExpand = isTextOverflowing && !isTextExpanded;

    return (
      <div className={classes.wrapper}>
        <div className={classes.resultPanel}>
          <div className={classes.resultHeader}>
            <h2 className={classes.resultHeaderText}>
              Vos résultats du test approfondi avec l&apos;IA
            </h2>
          </div>
          <div className={classes.resultBody}>
            <div
              ref={textContentRef}
              style={{
                maxHeight: shouldShowExpand ? 296 : 'none',
                overflow: shouldShowExpand ? 'hidden' : 'visible',
                position: 'relative',
              }}>
              <div className={classes.resultContent}>{ReactHtmlParser(synthesisText)}</div>
              {shouldShowExpand && <div className={classes.resultFade} aria-hidden="true" />}
            </div>

            {isTextOverflowing && isTextExpanded && (
              <button
                type="button"
                className={classes.collapseButton}
                onClick={() => setIsTextExpanded(false)}>
                Voir moins
              </button>
            )}
          </div>
        </div>

        {shouldShowExpand && (
          <button
            type="button"
            className={classes.expandButton}
            onClick={() => setIsTextExpanded(true)}>
            Afficher le reste de mes résultats
            <FormatListBulletedIcon style={{ width: 16, height: 16 }} />
          </button>
        )}
      </div>
    );
  }

  if (isCheckingExistence) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={classes.wrapper}>
        <div className={`${classes.panel} ${classes.panelLoading}`}>
          <div className={classes.loadingRow}>
            <CircularProgress size={28} className={classes.loadingSpinner} />
            <span className={classes.loadingText}>Analyse en cours</span>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.panel}>
          <div className={classes.copyBlock}>
            <h2 className={classes.title}>Impossible de générer la synthèse</h2>
            <p className={classes.errorText}>{errorMessage}</p>
            <button
              type="button"
              className={classes.retryButton}
              onClick={() => {
                setErrorMessage(null);
                handleGenerateSynthesis();
              }}>
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.panel}>
        <div className={classes.badge}>
          <SparkleIcon className={classes.badgeIcon} />
          <span className={classes.badgeText}>Innovation IA</span>
        </div>

        <div className={classes.copyBlock}>
          <h2 className={classes.title}>
            Explorez de nouvelles frontières avec la force de l&apos;IA
          </h2>
          <p className={classes.description}>
            Optimisez vos compétences grâce à des parcours de formation innovants intégrés à votre
            flux de travail.
          </p>
        </div>

        <button type="button" className={classes.ctaButton} onClick={handleGenerateSynthesis}>
          Je veux approfondir mes résultats
          <RocketLaunch className={classes.ctaIcon} size={16} weight="regular" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default IASynthesisBlock;
